<script lang='ts'>
import { computed, defineComponent } from '@vue/composition-api';
import TextView from '@/pages/main/feedbackportal/components/TextView.vue';
import { generateGitStyleMarkdownDiff } from '@/_core/utils/markdown/descriptionDiff';

interface IProps {
    oldText: string;
    newText: string;
    showDiff: boolean;
    height: string;
    emptyMessage: string;
    noChangesMessage: string;
}

/**
 * DiffViewer - Reusable component for displaying text diffs
 *
 * Renders either a diff view (with ins/del highlighting) or plain text,
 * using the shared diff styling from _diff-content.scss.
 *
 * @prop oldText - The original/before text
 * @prop newText - The new/after text
 * @prop showDiff - When true, shows diff view; when false, shows newText as plain
 * @prop height - CSS height for the content container (default: '50vh')
 * @prop emptyMessage - Message to show when both texts are empty
 * @prop noChangesMessage - Message to show when texts are identical
 */
const DiffViewer = defineComponent({
    name: 'DiffViewer',
    components: { TextView },
    props: {
        oldText: { type: String, default: '' },
        newText: { type: String, default: '' },
        showDiff: { type: Boolean, default: true },
        height: { type: String, default: '50vh' },
        emptyMessage: { type: String, default: 'No content' },
        noChangesMessage: { type: String, default: 'No changes' },
    },

    setup(props: IProps) {
        const renderedDiff = computed(() => {
            const oldText = props.oldText ?? '';
            const newText = props.newText ?? '';

            if (!oldText && !newText) {
                return props.emptyMessage;
            }

            return generateGitStyleMarkdownDiff(oldText, newText);
        });

        const displayContent = computed(() => {
            if (props.showDiff) {
                return renderedDiff.value;
            }
            // Plain view: show newText (or empty message)
            return props.newText || props.emptyMessage;
        });

        const containerStyle = computed(() => ({
            height: props.height,
        }));

        return {
            displayContent,
            containerStyle,
        };
    },
});

export default DiffViewer;
</script>

<template>
  <div class="diff-viewer" :style="containerStyle">
    <TextView
      :text="displayContent"
      :shouldClamp="false"
      :numberOfLines="0"
      :fillHeight="false"
    />
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/_diff-content.scss';

.diff-viewer {
    overflow-y: auto;
    overflow-x: hidden;
    padding: 16px 0;
    background-color: var(--v-grey5Light-base, #fafafa);
    border-radius: 4px;

    // Apply diff styling using ::v-deep to reach into TextView's rendered content
    ::v-deep {
        @include diff-content-styling;
    }
}
</style>
